import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import api from '../api';
import { Mail, Lock, AlertCircle, Eye, EyeOff, ArrowRight, ArrowLeft, Shield, Loader2, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import WordMark from '../components/WordMark';
import SocialAuthButtons from '../components/SocialAuthButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Device token storage
const DEVICE_TOKEN_KEY = 'matchitup_device_token';
const DEVICE_EMAIL_KEY = 'matchitup_device_email';

const cleanRedirectTarget = (target) => {
  if (!target) return null;

  const cleaned = String(target).trim().replace(/^\/+/, '');
  if (!cleaned) return null;
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) return null;
  if (cleaned === 'profile-setup') return 'profile-edit';
  if (cleaned.startsWith('social-done')) return 'dashboard';

  return cleaned;
};

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();

  // Surface OAuth errors that came back from the backend callback
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const oauthError = sp.get('oauth_error');
    if (oauthError) {
      toast.error(oauthError);
    }
  }, []);
  
  // Get redirect parameter from URL - use useEffect to capture on mount
  const [redirectTo, setRedirectTo] = useState(null);
  
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get('redirect');
    if (redirect) {
      setRedirectTo(redirect);
    }
  }, []);
  
  // Flow states: 'email' | 'otp' | 'password'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingDevice, setCheckingDevice] = useState(true);
  const [cooldown, setCooldown] = useState(0);
  const [userName, setUserName] = useState('');
  const [rateLimited, setRateLimited] = useState(false);
  // Sprint 13.0: surface "this account uses Google/LinkedIn" hint
  const [providerHint, setProviderHint] = useState(null); // { providers:[...], has_password:bool } | null
  
  const otpRefs = useRef([]);

  const resolvePostLoginDestination = async (authToken, fallbackRedirect = null) => {
    const safeRedirect = cleanRedirectTarget(fallbackRedirect);
    if (safeRedirect && safeRedirect !== 'profile-edit') {
      return `/${safeRedirect}`;
    }
    try {
      const statusResponse = await api.get(`${API}/profile/completion-status`, {
        withCredentials: true,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      const isComplete = !!statusResponse.data?.is_complete;
      if (safeRedirect === 'profile-edit' && isComplete) {
        return '/dashboard';
      }
      if (safeRedirect === 'profile-edit') {
        return '/profile-edit';
      }
      return safeRedirect ? `/${safeRedirect}` : '/dashboard';
    } catch {
      if (safeRedirect === 'profile-edit') {
        return '/profile-edit';
      }
      return safeRedirect ? `/${safeRedirect}` : '/dashboard';
    }
  };

  // Check for trusted device on mount
  useEffect(() => {
    const checkTrustedDevice = async () => {
      // Capture redirect early
      const searchParams = new URLSearchParams(window.location.search);
      const redirect = searchParams.get('redirect');
      
      const savedToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      const savedEmail = localStorage.getItem(DEVICE_EMAIL_KEY);
      
      if (savedToken && savedEmail) {
        try {
          // Check if device is still trusted
          const response = await api.post(`${API}/auth/check-device`, {
            email: savedEmail,
            device_token: savedToken
          }, { withCredentials: true });
          
          if (response.data.is_trusted && response.data.user_exists) {
            // Auto-login
            setEmail(savedEmail);
            setUserName(response.data.user_name);
            
            const loginResponse = await api.post(`${API}/auth/auto-login`, {
              email: savedEmail,
              device_token: savedToken
            }, { withCredentials: true });
            
            if (loginResponse.data.token) {
              loginWithToken(loginResponse.data.token, loginResponse.data.user, loginResponse.data.refresh_token);
              toast.success(`Welcome back, ${response.data.user_name}!`);
              // Use redirect from URL params captured at mount
              const destination = await resolvePostLoginDestination(loginResponse.data.token, redirect);
              navigate(destination);
              return;
            }
          }
        } catch (err) {
          // Device not trusted or expired, clear storage
          localStorage.removeItem(DEVICE_TOKEN_KEY);
          localStorage.removeItem(DEVICE_EMAIL_KEY);
        }
      }
      setCheckingDevice(false);
    };
    
    checkTrustedDevice();
  }, [navigate, loginWithToken]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Auto-focus OTP inputs
  useEffect(() => {
    if (step === 'otp' && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setProviderHint(null);
    setLoading(true);

    // Fire check-email in parallel — used purely to surface a hint banner. Never blocks login.
    axios
      .post(`${API}/auth/check-email`, { email }, { withCredentials: true })
      .then((r) => {
        const data = r.data || {};
        if (data.exists && Array.isArray(data.providers) && data.providers.length > 0) {
          setProviderHint({ providers: data.providers, has_password: !!data.has_password });
        }
      })
      .catch(() => {});

    try {
      const savedToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      
      const response = await api.post(`${API}/auth/send-login-otp`, {
        email: email,
        device_token: savedToken
      }, { withCredentials: true });
      
      if (response.data.device_trusted) {
        // Device is trusted - auto login
        const loginResponse = await api.post(`${API}/auth/auto-login`, {
          email: email,
          device_token: savedToken
        }, { withCredentials: true });
        
        if (loginResponse.data.token) {
          loginWithToken(loginResponse.data.token, loginResponse.data.user, loginResponse.data.refresh_token);
          toast.success('Welcome back!');
          // Read redirect directly from URL
          const searchParams = new URLSearchParams(window.location.search);
          const redirectParam = searchParams.get('redirect') || redirectTo;
          const destination = await resolvePostLoginDestination(loginResponse.data.token, redirectParam);
          navigate(destination);
          return;
        }
      }
      
      // OTP sent - move to OTP step
      // In dev/staging (no email provider), the OTP is returned directly in the response
      if (response.data.dev_otp) {
        toast.success(`Dev mode: Your OTP is ${response.data.dev_otp}`, { duration: 30000 });
      } else {
        toast.success('Verification code sent to your email');
      }
      setStep('otp');
      setCooldown(60);
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send verification code';
      
      // If rate limited (429), show error but do NOT force user to OTP step.
      // An unregistered user could hit the rate limit before the 404 check
      // fires — trapping them on the OTP screen for an account that doesn't exist.
      if (err.response?.status === 429) {
        setRateLimited(true);
        const match = errorMsg.match(/(\d+) seconds/);
        const secs = match ? parseInt(match[1]) : 3600;
        setCooldown(secs);
        // Keep user on the email step so they can see the "Sign Up" link
        setError(`Too many requests. Please try again in ${Math.ceil(secs / 60)} minutes. If you are trying to create a new account, please click "Sign Up" below.`);
      } else if (err.response?.status === 404) {
        // User not found — direct to sign up, do not suggest password login (account doesn't exist)
        setError('No account found with this email. Please check your spelling or sign up for a new account.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) newOtp[index + i] = digit;
      });
      setOtp(newOtp);
      
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }
    
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      const response = await api.post(`${API}/auth/verify-login-otp`, {
        email: email,
        otp: otpCode,
        trust_device: trustDevice,
        device_info: navigator.userAgent
      }, { withCredentials: true });
      
      if (response.data.token) {
        // Save device token if trusted
        if (response.data.device_token) {
          localStorage.setItem(DEVICE_TOKEN_KEY, response.data.device_token);
          localStorage.setItem(DEVICE_EMAIL_KEY, email);
        }
        
        loginWithToken(response.data.token, response.data.user, response.data.refresh_token);
        
        if (response.data.account_reactivated) {
          toast.success('Welcome back! Your account has been reactivated.');
        } else {
          toast.success('Login successful!');
        }
        
        // IMPORTANT: Always honor the redirect if present
        // Read directly from URL to ensure we have the latest value
        const searchParams = new URLSearchParams(window.location.search);
        const redirectParam = searchParams.get('redirect') || redirectTo;
        
        if (redirectParam) {
          const destination = await resolvePostLoginDestination(response.data.token, redirectParam);
          navigate(destination);
          return;
        }
        
        // Always go to dashboard — Browse owns the profile-completion gate now.
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid verification code';
      // If expired while rate-limited, give clearer guidance
      if (rateLimited && (errorMsg.toLowerCase().includes('expired') || errorMsg.toLowerCase().includes('no verification code'))) {
        setError('Your code has expired. Use the "Use password instead" option below, or wait for the rate limit to reset and request a new code.');
      } else {
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginResponse = await login(email, password);
      toast.success('Welcome back!');
      
      // Read redirect directly from URL to ensure we have it
      const searchParams = new URLSearchParams(window.location.search);
      const redirectParam = searchParams.get('redirect') || redirectTo;
      const destination = await resolvePostLoginDestination(loginResponse?.token || localStorage.getItem('token'), redirectParam);
      navigate(destination);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    
    setLoading(true);
    try {
      await api.post(`${API}/auth/send-login-otp`, { email }, { withCredentials: true });
      toast.success('New verification code sent');
      setCooldown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to resend code';
      // If rate limited, a code is already active - let user enter it
      if (err.response?.status === 429) {
        setRateLimited(true);
        const match = errorMsg.match(/(\d+) seconds/);
        if (match) setCooldown(parseInt(match[1]));
        else setCooldown(3600);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking device
  if (checkingDevice) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#E8430A] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Checking your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center px-4 py-12 font-mono text-[#0F172A]">
      <Helmet>
        <title>Log In | Match It Up</title>
        <meta name="description" content="Log in to Match It Up — AI-powered professional matchmaking. Find your next business partner, investor, or collaborator." />
        <meta property="og:title" content="Log In | Match It Up" />
        <meta property="og:description" content="Log in to your Match It Up account. Less noise, more signal, better introductions." />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity"
            data-testid="logo-home-btn"
          >
            <WordMark size="md" />
          </button>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-[#0F172A] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Welcome Back
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-[#0F172A] font-bold">
            {step === 'email' && 'Enter your email to login'}
            {step === 'otp' && (rateLimited ? 'Enter the code from your last email' : 'Enter the code sent to your email')}
            {step === 'password' && 'Enter your password'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-8">
          {/* Social Login — hidden until OAuth is ready for production */}
          <AnimatePresence mode="wait">
            {/* Step 1: Email */}
            {step === 'email' && (
              <motion.form
                key="email-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleEmailSubmit}
                className="space-y-6"
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="login-error">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                      className="w-full bg-white border border-slate-200 focus:border-[#E8430A] focus:ring-0 rounded-xl py-3 pl-12 pr-4 transition-all"
                      placeholder="your@email.com"
                      data-testid="login-email-input"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#E8430A] hover:bg-[#E8430A] text-white rounded-xl px-8 py-4 font-black uppercase tracking-tight border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] transition-all duration-300 transform hover:-translate-y-0.5 hover:-translate-x-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:translate-x-0 disabled:hover:shadow-xl shadow-slate-200/50 flex items-center justify-center gap-2"
                  data-testid="login-continue-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                {/* Social auth — Google + LinkedIn */}
                <div className="mt-4">
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-[#0F172A]/10" />
                    <span className="text-xs font-bold text-[#0F172A]/40 uppercase tracking-wider">or continue with</span>
                    <div className="flex-1 h-px bg-[#0F172A]/10" />
                  </div>
                  <SocialAuthButtons data-testid="social-auth-buttons" />
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('password')}
                    className="text-sm text-slate-500 hover:text-[#E8430A] transition-colors"
                    data-testid="use-password-link"
                  >
                    Use password instead
                  </button>
                </div>
              </motion.form>
            )}

            {/* Step 2: OTP */}
            {step === 'otp' && (
              <motion.form
                key="otp-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleOtpSubmit}
                className="space-y-6"
              >
                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setError(''); setRateLimited(false); }}
                  className="flex items-center gap-2 text-slate-600 hover:text-[#E8430A] transition-colors mb-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {providerHint && (
                  <div
                    className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2"
                    data-testid="provider-hint-banner"
                  >
                    <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      This account is also linked to{' '}
                      <strong className="capitalize">{providerHint.providers.join(' and ')}</strong>.{' '}
                      You can sign in faster with one of those buttons next time.
                    </p>
                  </div>
                )}

                {rateLimited && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="otp-rate-limit-notice">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Too many requests — no new code was sent</p>
                      <p className="text-sm text-amber-700 mt-1">
                        You&apos;ve already requested the maximum number of codes. Enter the 6-digit code from your <strong>last email</strong> — it&apos;s valid for 60 minutes. Can&apos;t find it? Check your spam folder.
                      </p>
                      {cooldown > 3600 ? null : cooldown > 60 ? (
                        <p className="text-xs text-amber-600 mt-1">You can request a new code in ~{Math.ceil(cooldown / 60)} min.</p>
                      ) : cooldown > 0 ? (
                        <p className="text-xs text-amber-600 mt-1">You can request a new code in {cooldown}s.</p>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 mb-4">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-600">{email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3 text-center">
                    Enter 6-digit code
                  </label>
                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => otpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-bold bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl transition-all"
                        data-testid={`otp-input-${index}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <input
                    type="checkbox"
                    id="trustDevice"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="w-5 h-5 text-[#E8430A] border-slate-300 rounded focus:ring-[#E8430A]"
                  />
                  <label htmlFor="trustDevice" className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    Remember this device for 30 days
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full bg-[#E8430A] hover:bg-[#E8430A] text-white rounded-xl px-8 py-4 font-black uppercase tracking-tight border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] transition-all duration-300 transform hover:-translate-y-0.5 hover:-translate-x-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:translate-x-0 disabled:hover:shadow-xl shadow-slate-200/50 flex items-center justify-center gap-2"
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={resendOtp}
                    disabled={cooldown > 0 || loading}
                    className="text-sm text-slate-500 hover:text-[#E8430A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get the code? Resend"}
                  </button>
                </div>
              </motion.form>
            )}

            {/* Step 3: Password (Secondary) */}
            {step === 'password' && (
              <motion.form
                key="password-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handlePasswordSubmit}
                className="space-y-6"
              >
                <button
                  type="button"
                  onClick={() => { setStep('email'); setError(''); }}
                  className="flex items-center gap-2 text-slate-600 hover:text-[#E8430A] transition-colors mb-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to OTP login
                </button>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                      className="w-full bg-white border border-slate-200 focus:border-[#E8430A] focus:ring-0 rounded-xl py-3 pl-12 pr-4 transition-all"
                      placeholder="your@email.com"
                      data-testid="login-email-password-input"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-12 transition-all"
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E8430A] hover:bg-[#E8430A] text-white rounded-xl px-8 py-4 font-black uppercase tracking-tight border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] transition-all duration-300 transform hover:-translate-y-0.5 hover:-translate-x-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:translate-x-0 disabled:hover:shadow-xl shadow-slate-200/50 flex items-center justify-center gap-2"
                  data-testid="login-password-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Login'
                  )}
                </button>

                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#E8430A] transition-colors">
                    Forgot your password?
                  </Link>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-[#E8430A] hover:text-[#d13a08] font-semibold" data-testid="login-register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
